import { FC, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Box, Flex, Grid, Img, Portal, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { Contest, ContestParticipant } from 'models';
import routes from 'routes';
import { stylizeText } from 'utils/stylizeText';

import CardPlaceholder from 'components/builder/CardPlaceholder';
import LeaderboardCardPlaceholder from 'components/builder/LeaderboardCardPlaceholder';
import Squad from 'components/builder/Squad/Squad';

import { useAppSelector } from 'hooks/app';

import { ReactComponent as NoneIcon } from 'icons/none.svg';

import ParticipantPosition from './ParticipantPosition';

type Props = {
  participant: ContestParticipant;
  position: number;
  contest?: Contest;
  isMe?: boolean;
};

const LeaderboardRow: FC<Props> = ({ participant, position, contest, isMe }) => {
  const [showParticipantDetail, setShowParticipantDetail] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  if (participant.status !== 'completed') {
    return null;
  }

  const date = searchParams.get('date');
  const friends = searchParams.get('friends') === 'true';

  const booster = participant.booster;
  const user = participant.user;

  useEffect(() => {
    if (showParticipantDetail) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [showParticipantDetail]);

  if (showParticipantDetail) {
    const date = dayjs(participant.updated).tz('America/Los_Angeles');
    const dateStr = date.format('MMM D');

    return (
      <Portal>
        <Squad
          participation={participant}
          cards={participant.cards.cardsWithStatus}
          pos="fixed"
          top="0"
          bottom="0"
          left="0"
          right="0"
          bg="var(--chakra-colors-bg)"
          onClose={() => setShowParticipantDetail(false)}
          title={`${participant.user.username}`}
          description={`Start Bench Cut · ${dateStr}`}
          overflow="auto"
          detailed
          zIndex="100"
          contestStatus={'completed'}
          contest={contest}
          className="portal"
        />
      </Portal>
    );
  }

  return (
    <>
      <Box
        p="16px 0"
        borderBottom="1px solid rgba(255,255,255,0.05)"
        onClick={() => setShowParticipantDetail(true)}
        cursor="pointer"
        transition="all 0.2s ease-in-out"
        position="relative"
        _active={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
      >
        {isMe && (
          <Box
            position="absolute"
            top="-0.5px"
            left="-12px"
            right="-12px"
            bottom="-0.5px"
            backgroundColor="#F83C3C"
            borderBottom="0.5px solid #646464"
            borderTop="0.5px solid #646464"
            zIndex="0"
          />
        )}
        <Grid
          zIndex="1"
          position="relative"
          gap="10px"
          alignItems="flex-start"
          justifyContent="center"
          gridTemplateColumns="28px 1fr"
          color="white"
          maxW="375px"
          mx="auto"
        >
          <Flex align="end" h="24px" alignItems="center">
            <ParticipantPosition position={position + 1} isMe={!!isMe} isSimple />
          </Flex>
          <Flex
            gap="12px"
            align="flex-end"
            justifyContent="center"
            fontSize="14px"
            fontWeight="800"
            lineHeight="16.94px"
            flexShrink="0"
            flexGrow="0"
            flex="1"
            flexDirection="column"
          >
            <Flex flex="1" align="center" gap="8px" w="100%" justifyContent="space-between">
              <Flex
                gap="8px"
                flex="1"
                align="center"
                justify="flex-start"
                maxW="100%"
                minW="0"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`${routes.profile}/${user.id}`, {
                    state: {
                      from: `/leaderboard/monthly/${date}?friends=${!!friends}&league=all`,
                    },
                  });
                }}
                borderRadius="12px"
                cursor="pointer"
                transition="all 0.2s ease-in-out"
                _active={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
              >
                <Flex
                  w="24px"
                  h="24px"
                  borderRadius="8px"
                  background={isMe ? '#FFF' : '#FFFFFF1A'}
                  fontSize="13px"
                  fontWeight="800"
                  justify="center"
                  align="center"
                  color={isMe ? '#000' : '#FFFFFFCC'}
                  flexShrink="0"
                  textTransform="uppercase"
                  backgroundPosition="center center"
                  backgroundSize="cover"
                  backgroundImage={user.avatarUrl ? `url(${user.avatarUrl})` : undefined}
                >
                  {user.avatarUrl ? null : user.username[0]}
                </Flex>
                <Box
                  flex="1"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  fontSize="11px"
                  fontWeight="400"
                  lineHeight="1.2"
                  fontFamily="Special Gothic Expanded One"
                  textTransform="uppercase"
                >
                  {stylizeText(user.username)}
                </Box>
              </Flex>
              <Flex
                align="center"
                gap="2px"
                marginLeft="auto"
                fontSize="12px"
                fontWeight="400"
                lineHeight="1.2"
                fontFamily="Special Gothic Expanded One"
                textTransform="uppercase"
              >
                {participant.totalPoints}
              </Flex>
            </Flex>

            <Flex justifyContent="flex-end" gap="3px" w="100%">
              {/* {startCards.map((card) => (
                <LeaderboardCardPlaceholder key={card.id} minW="60px" card={card} />
              ))}
              {benchCard && <LeaderboardCardPlaceholder bgColor="#4F4F4F" color="#D2C9C9" card={benchCard} />} */}
              {participant.cards.cardsWithStatus
                .filter((card) => card.status !== 'cut')
                .sort((a, b) => b.totalPoints - a.totalPoints)
                .map((card, index) => (
                  <LeaderboardCardPlaceholder
                    key={card.id}
                    minW="60px"
                    card={card}
                    bgColor={card.status === 'bench' ? '#4F4F4F' : '#fff'}
                    hidePoints={index > 2}
                  />
                ))}
              {booster ? (
                <Grid w="60px" gridTemplateColumns="1fr" gridTemplateRows="1fr 11px" gap="4px" pos="relative">
                  <Box
                    pos="relative"
                    w="60px"
                    h="60px"
                    backgroundColor={booster.backgroundColor ?? 'transparent'}
                    borderRadius="16px"
                    backgroundImage={`url(${booster.image})`}
                    backgroundSize="cover"
                    backgroundPosition="center center"
                    border="1px solid rgba(255, 255, 255, 0.1)"
                    overflow="hidden"
                  >
                    {booster.icon && (
                      <Img
                        src={booster.icon}
                        pos="absolute"
                        top="50%"
                        left="50%"
                        w="60px"
                        h="60px"
                        objectFit="cover"
                        objectPosition="center center"
                        transform="translate(-50%, -50%)"
                      />
                    )}
                  </Box>
                  <Box w="100%" textAlign="center" overflow="hidden" maxW="100%" px="2px">
                    <Text
                      color="white"
                      opacity="0.6"
                      fontSize="9px"
                      fontWeight="regular"
                      lineHeight="1.2"
                      textTransform="capitalize"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      maxW="100%"
                    >
                      {`${parseInt(booster.attributeMultiplier)}x${booster.attributeDisplay}`}
                    </Text>
                  </Box>
                </Grid>
              ) : (
                <Box
                  pos="relative"
                  ml="-2px"
                  w="60px"
                  h="60px"
                  backgroundColor="#111111"
                  borderRadius="14px"
                  boxShadow="4px 0px 20px 0px #00000080"
                >
                  <Box as={NoneIcon} pos="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" />
                </Box>
              )}
            </Flex>
          </Flex>
        </Grid>
      </Box>
    </>
  );
};

export default LeaderboardRow;
